/**
 * Date formatting utilities for consistent date/time display across the application
 */

/**
 * Format time as HH:MM
 * @param date Date object to format
 * @returns Formatted time string (e.g., "20:00")
 */
export function formatTime(date: Date): string {
    const hours = date.getHours().toString().padStart(2, '0');
    const minutes = date.getMinutes().toString().padStart(2, '0');
    return `${hours}:${minutes}`;
}

/**
 * Format date as M/D
 * @param date Date object to format
 * @returns Formatted date string (e.g., "12/11")
 */
export function formatDate(date: Date): string {
    const month = date.getMonth() + 1;
    const day = date.getDate();
    return `${month}/${day}`;
}

/**
 * Format date and time as M/D HH:MM
 * @param date Date object to format
 * @returns Formatted datetime string (e.g., "12/11 20:00")
 */
export function formatDateTime(date: Date): string {
    return `${formatDate(date)} ${formatTime(date)}`;
}

/**
 * Format a schedule time range
 * @param startTime Start time string (ISO format)
 * @param endTime End time string (ISO format)
 * @param todayLabel Label to use for today's date (e.g., "本日" or "Today")
 * @returns Formatted time range string (e.g., "本日 20:00-22:00" or "12/11 20:00-22:00")
 */
export function formatScheduleTimeRange(
    startTime: string,
    endTime: string,
    todayLabel: string = '本日'
): string {
    const start = new Date(startTime);
    const end = new Date(endTime);
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const scheduleDate = new Date(start.getFullYear(), start.getMonth(), start.getDate());

    const timeRange = `${formatTime(start)}-${formatTime(end)}`;

    if (scheduleDate.getTime() === today.getTime()) {
        return `${todayLabel} ${timeRange}`;
    } else {
        return `${formatDate(start)} ${timeRange}`;
    }
}

/**
 * Format a schedule for dropdown display
 * @param startTime Start time string (ISO format)
 * @param endTime End time string (ISO format)
 * @returns Formatted string for dropdown (e.g., "12/11 20:00 〜 12/11 22:00")
 */
export function formatScheduleDropdown(startTime: string, endTime: string): string {
    const start = new Date(startTime);
    const end = new Date(endTime);
    return `${formatDateTime(start)} 〜 ${formatDateTime(end)}`;
}

/**
 * Check if a date is today
 * @param date Date to check
 * @returns true if the date is today
 */
export function isToday(date: Date): boolean {
    const now = new Date();
    return (
        date.getFullYear() === now.getFullYear() &&
        date.getMonth() === now.getMonth() &&
        date.getDate() === now.getDate()
    );
}

const JST_DATE_TIME = new Intl.DateTimeFormat('ja-JP', {
    timeZone: 'Asia/Tokyo',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
});

/**
 * Parse an API timestamp. Older endpoints return naive UTC strings without a
 * zone suffix; those are interpreted as UTC rather than browser-local time.
 */
export function parseApiTimestamp(value: string): Date {
    const hasZone = /([zZ]|[+-]\d{2}:?\d{2})$/.test(value);
    return new Date(hasZone ? value : `${value}Z`);
}

/**
 * Format an API timestamp in Japan Standard Time as YYYY/MM/DD HH:MM.
 * Returns an empty string for null/invalid values.
 */
export function formatJstDateTime(value: string | null | undefined): string {
    if (!value) return '';
    const date = parseApiTimestamp(value);
    if (Number.isNaN(date.getTime())) return '';
    return JST_DATE_TIME.format(date);
}

/**
 * Convert a value from an <input type="datetime-local"> interpreted as JST into ISO8601 with +09:00.
 */
export function jstLocalInputToIso(value: string): string {
    const normalized = value.length === 16 ? `${value}:00` : value;
    return `${normalized}+09:00`;
}

/**
 * Current JST time formatted for an <input type="datetime-local"> (YYYY-MM-DDTHH:MM).
 */
export function nowAsJstLocalInput(now: Date = new Date()): string {
    const jst = new Date(now.getTime() + 9 * 60 * 60 * 1000);
    return jst.toISOString().slice(0, 16);
}

/**
 * Current JST month as YYYY-MM.
 */
export function currentJstMonth(now: Date = new Date()): string {
    return nowAsJstLocalInput(now).slice(0, 7);
}
